import { buildFilterUrl } from '@cannect/constants/productFilters'
import { useAuth } from '@cannect/hooks/AuthContext'
import { useCannectAssistant } from '@cannect/hooks/useCannectAssistant'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import useQueryString from '@cannect/hooks/useQueryString'
import useSignedUser from '@cannect/hooks/useSignedUser'
import { useTokenApi } from '@cannect/hooks/useTokenApi'
import { TLinkItem } from '@cannect/new-components/molecules/LinkItem/types'
import { usePublicRender } from '@cannect/utils/renderActions'
import { deleteUser } from 'hooks/useSessionUser'
import { useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'
import api from 'services/api'

const PRODUCTS_MENU_PATH = buildFilterUrl({ class_id: 10, translated_name: '' })

export const useHeader = () => {
  const history = useHistory()
  const location = useLocation()
  const { filters } = useQueryString()
  const { signedUser } = useSignedUser()
  const { deleteTokenApi, tokenApi } = useTokenApi()
  const { prescription, setStep, loading } = useCannectAssistant()
  const { getRoles } = useAuth()
  const { getLastOrder, lastOrderCreated, loadingCupon, clearOrderData, clearCart, anonOrderData } = usePrepareCart()
  const { isPublic: isPublicFlow } = usePublicRender()

  const signedUserName = useMemo(() => {
    const name = signedUser?.preferred_name || signedUser?.name || ''
    return name.split(' ')[0]
  }, [signedUser])

  const isAssistantFlow = useMemo(() => location.pathname.startsWith('/produtos_assistente'), [location])

  const isDigitalJourneyFlow = useMemo(() => location.pathname.includes('jornada-digital'), [location?.pathname])

  const getProductQuantity = (products: any) => {
    return (
      // @ts-expect-error - we need to type products
      products?.reduce((acc, curr) => {
        const itemQuantity = curr.quantity || curr.quantity_item || 0
        return acc + itemQuantity
      }, 0) || 0
    )
  }

  const isLogged = useMemo(() => !!tokenApi, [tokenApi])

  const cartProductsQuantity = useMemo(() => {
    if (isAssistantFlow) {
      return getProductQuantity(prescription?.products)
    }

    if (!isLogged) {
      return getProductQuantity(anonOrderData?.items)
    }

    return getProductQuantity(lastOrderCreated?.items)
  }, [isAssistantFlow, prescription?.products, lastOrderCreated?.items, isLogged, anonOrderData?.items])

  const prescriberHasLogo = useMemo(
    () => prescription?.prescriber?.profilePicture,
    [prescription?.prescriber?.profilePicture]
  )

  const handleLogout = () => {
    try {
      clearCart()
      clearOrderData()
      localStorage.removeItem('selectedProducts')
      localStorage.removeItem('selectedProductsMedice')
      localStorage.removeItem('@CANNECT:CHECKOUT_CART')
      localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART_ID')
      localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART')
      localStorage.removeItem('@CANNECT:RECIPE_WARNING')
      localStorage.removeItem('@CANNECT:CREDIT_SELECTION')
      localStorage.removeItem('@CANNECT:CARD_PIX_SELECTION')
      localStorage.removeItem('@CANNECT:CARD_BOLETO_SELECTION')
      localStorage.removeItem('@CANNECT:TWO_CARDS_SELECTION')
      localStorage.removeItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID')
    } catch (e) {
      // noop
    }
    deleteTokenApi()
    deleteUser()
    getRoles()
    history.push('/login')
  }

  const handleAssistantContinue = () => {
    if (isAssistantFlow) {
      window.dispatchEvent(new CustomEvent('open-handoff-modal'))
      return
    }
    // Fallback: fluxo antigo
    const validateStep = prescriberHasLogo ? 2 : 1
    setStep((step) => step + validateStep)
    let url = '/assistente'
    if (filters.template) {
      url += `?template=${filters.template}`
    }
    history.push(url)
  }

  const handlePrescriberAccess = async () => {
    let assistantToken: string | undefined
    try {
      const cannectToken = localStorage.getItem('@CANNECT_TOKEN_API')
      if (cannectToken) {
        const { data } = await api.post(
          '/generate-assistant-token',
          {},
          { headers: { Authorization: `Bearer ${cannectToken}` } }
        )
        assistantToken = data?.token
      }
    } catch (err) {
      return
    }

    const assistantBaseUrl = import.meta.env.VITE_APP_HEALTH_PROFESSIONAL_BASE_URL || 'http://localhost:3003/'
    if (assistantToken) {
      window.open(`${assistantBaseUrl}#/login?token=${assistantToken}`, '_blank')
    } else {
      window.open(`${assistantBaseUrl}#/login`, '_blank')
    }
  }

  useEffect(() => {
    if (tokenApi) {
      getLastOrder()
    }
  }, [tokenApi])

  /**
   * Navegação principal do header (Figma "Novo Marketplace", node 4161:23973).
   * Substitui o menu anterior (Produtos / Agendar Consulta / Profissionais de saúde /
   * Parceiros e soluções / Blog), que correspondia à versão marcada como oculta no protótipo.
   */
  const menuConfig: TLinkItem[] = [
    { title: 'Produtos', path: PRODUCTS_MENU_PATH },
    { title: 'Cuida', path: '/cuida' },
    { title: 'Conteúdos', path: 'https://blog.cannect.life', external: true }
  ]

  /** Ações à direita do header: pill outline + pill preenchido. */
  const headerCtas = {
    professionals: {
      title: 'Para profissionais',
      path: 'https://profissionaldasaude.cannect.life/',
      external: true
    } as TLinkItem,
    appointment: { title: 'Fazer Consulta', path: '/agendamento' } as TLinkItem
  }

  return {
    menuConfig,
    headerCtas,
    signedUserName,
    handleLogout,
    isLogged,
    cartProductsQuantity,
    isAssistantFlow,
    handleAssistantContinue,
    handlePrescriberAccess,
    isLoadingCartQuantity: loadingCupon || loading,
    isPublicFlow,
    isDigitalJourneyFlow
  }
}
